import { Helmet } from 'react-helmet-async';

import {
  DEFAULT_DESCRIPTION,
  DEFAULT_KEYWORDS,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  SITE_NAME,
  SITE_URL,
} from '@/config/seo';

export interface SeoProps {
  /** 페이지 고유 제목. 생략 시 기본 타이틀 사용. "<title> | 프람트테크놀로지" 형태로 합성됨 */
  title?: string;
  description?: string;
  /** canonical/OG URL 생성을 위한 경로(예: '/ai'). 생략 시 canonical 미출력 */
  path?: string;
  /** OG 이미지 경로(절대 URL 또는 '/'로 시작하는 상대경로) */
  image?: string;
  /** 검색엔진 색인 제외 여부 */
  noindex?: boolean;
  /** 페이지별 구조화 데이터(JSON-LD). 객체 또는 객체 배열. 답변 엔진(GEO/AEO) 최적화용 */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

/** 루트 도메인과 합성해 절대 URL 을 만든다 */
function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//.test(pathOrUrl)) return pathOrUrl;
  return `${SITE_URL}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
}

/**
 * 라우트별 메타 태그(title/description/canonical/OG/Twitter)를 주입하는 재사용 컴포넌트.
 * 각 페이지 컴포넌트 최상단에서 <Seo .../> 형태로 사용한다.
 */
export default function Seo({ title, description, path, image, noindex, jsonLd }: SeoProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const desc = description ?? DEFAULT_DESCRIPTION;
  const canonical = path ? absoluteUrl(path) : undefined;
  const ogImage = absoluteUrl(image ?? DEFAULT_OG_IMAGE);
  const jsonLdList = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <html lang="ko" />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={DEFAULT_KEYWORDS} />
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : <meta name="robots" content="index, follow" />}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="ko_KR" />
      {canonical && <meta property="og:url" content={canonical} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* 페이지별 구조화 데이터 (JSON-LD) */}
      {jsonLdList.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}
